// internationalization
import type { TFunction } from 'i18next';

// shared components
import type { DanishAddressFieldValue } from '@/components/ui/form';

// relative
import { unitTypeInputValueSchema } from './unitTypeInput.schema';
import type { UnitTypeInputDefinition } from './unitTypeInput.schema';

// libraries
import { z } from 'zod';

export function createEditUnitFormSchema(
  t: TFunction,
  unitTypeInputDefinitions: UnitTypeInputDefinition[],
) {
  return z
    .object({
      name: z.string().min(1, t('input.required', { ns: 'validation' })),
      description: z.string(),
      // No `.refine()` on address - unlike create, an existing unit's
      // location is optional to keep/change on edit (matches
      // editGatewayForm.schema.ts).
      address: z.custom<DanishAddressFieldValue>(),
      tags: z.array(z.string()),
      unitTypeInputs: z.record(z.string(), unitTypeInputValueSchema),
    })
    .superRefine((values, ctx) => {
      const invalidInputNames = Object.entries(values.unitTypeInputs)
        .filter(
          ([, inputData]) =>
            inputData.inputType === 'sensorId' &&
            !!inputData.sensorId &&
            !inputData.channelNumber,
        )
        .map(([inputId]) => {
          const definition = unitTypeInputDefinitions.find(
            (unitTypeInput) =>
              unitTypeInput.UnitTypeInputId === parseInt(inputId, 10),
          );
          return definition?.Name ?? inputId;
        });

      if (invalidInputNames.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['unitTypeInputs'],
          message: t('units.pleaseSelectChannelNumbers', {
            ns: 'units-gateways-sensors',
            names: invalidInputNames.join(', '),
          }),
        });
      }
    });
}

export type EditUnitFormValues = z.infer<
  ReturnType<typeof createEditUnitFormSchema>
>;
